import { parseStoryline } from './storyline.js';
/** The portal's sections, as its menu names them. */
export const WEB_SECTIONS = ['Κύπρος', 'Πολιτική', 'Διεθνή', 'Οικονομία', 'Αθλητικά', 'Ψυχαγωγία'];
/** The byline the portal prints on the station's own stories. */
export const WEB_BYLINE = 'ALPHANEWSLIVE';
/** The most paragraphs, takeaways and tags an article keeps. */
export const WEB_LIMITS = { paragraphs: 12, takeaways: 5, tags: 6, takeawayChars: 200 };
export const isWebSection = (s) => typeof s === 'string' && WEB_SECTIONS.includes(s);
const paras = (s) => s.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
const oneLine = (s) => s.replace(/\s+/g, ' ').trim();
/** The presenter's greeting that opens a paragraph ("Καλησπέρα σας.", "Καλημέρα κυρίες και κύριοι."): said on air, not written on the portal. */
const GREETING = /^(?:καλησπέρα|καλημέρα|καλό (?:μεσημέρι|απόγευμα|βράδυ)|χαίρετε|γεια σας)[^.!;]{0,40}[.!;]\s*/iu;
const unsaid = (p) => p.replace(GREETING, '').trim();
/** A statement as the article quotes it: the speaker, their role, their words. */
export function quoteOf(b) {
    const text = oneLine(b.text);
    if (!text)
        return '';
    const who = [b.name.trim(), b.role.trim()].filter(Boolean).join(', ');
    const words = /^[«"“]/.test(text) ? text : `«${text}»`;
    return who ? `${who}: ${words}` : words;
}
/** The first sentence of a paragraph, cut at a word when it runs long. */
export function firstSentence(p, max = WEB_LIMITS.takeawayChars) {
    const s = oneLine(p).split(/(?<=[.!;?…])\s+/u)[0] ?? '';
    if (s.length <= max)
        return s;
    const cut = s.slice(0, max);
    const at = cut.lastIndexOf(' ');
    return `${cut.slice(0, at > max / 2 ? at : max).replace(/[,·;:]$/, '')}…`;
}
/**
 * The plain draft: the presenter's and the voice-over's words as paragraphs, the statements as quotes, the first
 * one-line title as the subtitle, the reporter as the byline, the first sentences as the takeaways.
 */
export function draftWebArticle(title, script) {
    const blocks = parseStoryline(script);
    const paragraphs = [];
    let subtitle = '';
    let byline = '';
    for (const b of blocks) {
        if (b.kind === 'read')
            paragraphs.push(...paras(b.text).map(unsaid).filter(Boolean));
        else if (b.kind === 'sot') {
            const q = quoteOf(b);
            if (q)
                paragraphs.push(q);
        }
        else if (b.kind === 'title' && !subtitle) {
            const lines = b.lines.map((l) => l.trim()).filter(Boolean);
            if (lines.length === 1)
                subtitle = lines[0];
        }
        else if (b.kind === 'reporter' && !byline && b.name.trim())
            byline = b.name.trim();
    }
    const takeaways = [];
    for (const p of paragraphs) {
        const s = firstSentence(p);
        if (s && !takeaways.includes(s))
            takeaways.push(s);
        if (takeaways.length >= WEB_LIMITS.takeaways - 1)
            break;
    }
    return { title: oneLine(title), subtitle, paragraphs: paragraphs.slice(0, WEB_LIMITS.paragraphs), takeaways, section: 'Κύπρος', tags: [], byline: byline || WEB_BYLINE };
}
/** An article the AI answered, each field falling back to the plain draft's when the model left it empty or wrong. */
export function mergeWebArticle(plain, ai) {
    const strs = (v, max) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string').map(oneLine).filter(Boolean).slice(0, max) : []);
    const paragraphs = strs(ai.paragraphs, WEB_LIMITS.paragraphs);
    const takeaways = strs(ai.takeaways, WEB_LIMITS.takeaways);
    return {
        title: oneLine(ai.title ?? '') || plain.title,
        subtitle: oneLine(ai.subtitle ?? '') || plain.subtitle,
        paragraphs: paragraphs.length ? paragraphs : plain.paragraphs,
        takeaways: takeaways.length ? takeaways : plain.takeaways,
        section: isWebSection(ai.section) ? ai.section : plain.section,
        tags: strs(ai.tags, WEB_LIMITS.tags),
        byline: oneLine(ai.byline ?? '') || plain.byline,
    };
}
// ── The article's address on the portal: Greek letters to Latin the way the portal's own addresses spell them ──
const LATIN = { α: 'a', β: 'v', γ: 'g', δ: 'd', ε: 'e', ζ: 'z', η: 'i', θ: 'th', ι: 'i', κ: 'k', λ: 'l', μ: 'm', ν: 'n', ξ: 'x', ο: 'o', π: 'p', ρ: 'r', σ: 's', ς: 's', τ: 't', υ: 'y', φ: 'f', χ: 'ch', ψ: 'ps', ω: 'o' };
/** `Με διάσειση η 10χρονη` → `me-diaseisi-i-10chroni`. */
export function webSlug(title) {
    const plain = title
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/ου/g, 'ou')
        // αυ / ευ: "af" / "ef" before a voiceless consonant or at the end of a word, "av" / "ev" otherwise
        .replace(/(α|ε)υ(?=[θκξπστφχψ]|$|[^α-ω])/g, '$1f')
        .replace(/(α|ε)υ/g, '$1v')
        .replace(/μπ/g, 'b')
        .replace(/γγ|γκ/g, 'ng')
        .replace(/[α-ω]/g, (c) => LATIN[c] ?? c)
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    return plain.slice(0, 120).replace(/-+$/, '');
}
/** The portal's section folder in an address (`/cyprus/…`). */
export const WEB_SECTION_PATHS = { Κύπρος: 'cyprus', Πολιτική: 'politics', Διεθνή: 'world', Οικονομία: 'economy', Αθλητικά: 'sports', Ψυχαγωγία: 'entertainment' };
//# sourceMappingURL=web-article.js.map